import { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout';
import {
  Card, CardContent, CardHeader, CardTitle, CardDescription,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import { toast } from 'sonner';
import {
  ShieldCheck, MessageSquare, CheckCircle, XCircle,
} from 'lucide-react';
import { admin, getErrorMessage } from '../../utils/api';
import EvidenceViewer from '../shared/EvidenceViewer';

const _fmt = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso || '—'; }
};

function ResolveModal({ dispute, onOpenChange, onResolved }) {
  const [resolution, setResolution] = useState('approved');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const open = Boolean(dispute);

  const _close = () => {
    setResolution('approved');
    setNotes('');
    onOpenChange(null);
  };

  const _submit = async () => {
    setSubmitting(true);
    try {
      await admin.resolveDispute(dispute.id, {
        resolution,
        admin_notes: notes.trim() || undefined,
      });
      toast.success(`Dispute ${resolution}.`);
      onResolved && onResolved();
      _close();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to resolve dispute'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? null : _close())}>
      <DialogContent className="max-w-lg" data-testid="admin-resolve-modal">
        <DialogHeader>
          <DialogTitle>Resolve Dispute</DialogTitle>
          <DialogDescription>
            Approving reverses the withholding (adjustment.withheld_amount → $0) and
            refreshes the parent settlement's net_payable. Denying leaves the adjustment applied.
          </DialogDescription>
        </DialogHeader>

        {dispute && (
          <div className="space-y-3">
            <div className="rounded-md border bg-gray-50 p-3 text-xs space-y-1">
              <div><span className="font-medium">Dispute:</span> {dispute.id?.slice(0, 8)}</div>
              <div><span className="font-medium">Adjustment:</span> {dispute.adjustment_id?.slice(0, 8)}</div>
              <div><span className="font-medium">Filed:</span> {_fmt(dispute.created_at)}</div>
              <div className="pt-2 border-t"><span className="font-medium">Retailer comment:</span> {dispute.retailer_comment}</div>
            </div>
            {(dispute.retailer_evidence_urls?.length ?? 0) > 0 && (
              <EvidenceViewer urls={dispute.retailer_evidence_urls} title="Retailer evidence" />
            )}
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">Resolution</label>
              <div className="flex gap-2">
                <Button
                  variant={resolution === 'approved' ? 'default' : 'outline'}
                  onClick={() => setResolution('approved')}
                  className={resolution === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : ''}
                  data-testid="resolution-approve"
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Approve (reverse)
                </Button>
                <Button
                  variant={resolution === 'denied' ? 'default' : 'outline'}
                  onClick={() => setResolution('denied')}
                  className={resolution === 'denied' ? 'bg-red-600 hover:bg-red-700' : ''}
                  data-testid="resolution-deny"
                >
                  <XCircle className="w-4 h-4 mr-2" />
                  Deny
                </Button>
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-gray-700 mb-1 block">
                Admin notes (visible to retailer)
              </label>
              <Textarea
                rows={3}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Reason for approval / denial…"
                data-testid="resolution-notes"
              />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={_close} disabled={submitting}>
            Cancel
          </Button>
          <Button
            onClick={_submit}
            disabled={submitting}
            data-testid="resolution-submit-btn"
            className={resolution === 'approved' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
          >
            {submitting ? 'Saving…' : `Confirm ${resolution === 'approved' ? 'Approve' : 'Deny'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminDisputeReview({ user, onLogout }) {
  const [pending, setPending] = useState([]);
  const [resolved, setResolved] = useState([]);
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);

  const _load = async () => {
    try {
      setLoading(true);
      const [p, a, d] = await Promise.all([
        admin.listDisputes({ status: 'pending' }),
        admin.listDisputes({ status: 'approved' }),
        admin.listDisputes({ status: 'denied' }),
      ]);
      setPending(p.data?.disputes || []);
      setResolved([
        ...(a.data?.disputes || []),
        ...(d.data?.disputes || []),
      ].sort((x, y) => (y.resolved_at || '').localeCompare(x.resolved_at || '')));
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load disputes'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { _load(); }, []);

  const _renderRow = (d, showResolution = false) => (
    <TableRow key={d.id} data-testid={`admin-dispute-row-${d.id}`}>
      <TableCell className="font-mono text-xs">{d.id.slice(0, 8)}</TableCell>
      <TableCell className="font-mono text-xs">{d.adjustment_id?.slice(0, 8)}</TableCell>
      <TableCell className="font-mono text-xs">{d.retailer_id?.slice(0, 8)}</TableCell>
      <TableCell className="text-xs max-w-xs truncate" title={d.retailer_comment}>
        {d.retailer_comment}
      </TableCell>
      <TableCell>
        {(d.retailer_evidence_urls?.length ?? 0) > 0 ? (
          <Badge className="bg-blue-100 text-blue-800">
            {d.retailer_evidence_urls.length} file(s)
          </Badge>
        ) : (
          <span className="text-xs text-gray-400">—</span>
        )}
      </TableCell>
      <TableCell className="text-xs">{_fmt(d.created_at)}</TableCell>
      {showResolution ? (
        <>
          <TableCell>
            {d.resolution === 'approved' ? (
              <Badge className="bg-emerald-100 text-emerald-800">Approved</Badge>
            ) : (
              <Badge className="bg-red-100 text-red-800">Denied</Badge>
            )}
          </TableCell>
          <TableCell className="text-xs max-w-xs truncate" title={d.admin_notes}>
            {d.admin_notes || '—'}
          </TableCell>
        </>
      ) : (
        <TableCell>
          <Button
            size="sm"
            onClick={() => setTarget(d)}
            className="bg-emerald-600 hover:bg-emerald-700"
            data-testid={`admin-review-btn-${d.id}`}
          >
            Review
          </Button>
        </TableCell>
      )}
    </TableRow>
  );

  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="admin-disputes-page">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <ShieldCheck className="w-6 h-6 text-purple-600" />
            Dispute Review
          </h1>
          <p className="text-sm text-gray-600">
            Retailer disputes against refund adjustments. Approvals reverse the withhold and refresh net payable.
          </p>
        </div>

        <Tabs defaultValue="pending">
          <TabsList>
            <TabsTrigger value="pending" data-testid="tab-pending">
              Pending ({pending.length})
            </TabsTrigger>
            <TabsTrigger value="resolved" data-testid="tab-resolved">
              Resolved ({resolved.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pending">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <MessageSquare className="w-4 h-4 text-purple-600" />
                  Pending queue
                </CardTitle>
                <CardDescription>Awaiting admin resolution</CardDescription>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <p className="p-6 text-sm text-gray-500">Loading…</p>
                ) : pending.length === 0 ? (
                  <p className="p-6 text-sm text-gray-500" data-testid="admin-pending-empty">
                    No pending disputes.
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Dispute</TableHead>
                        <TableHead>Adjustment</TableHead>
                        <TableHead>Retailer</TableHead>
                        <TableHead>Comment</TableHead>
                        <TableHead>Evidence</TableHead>
                        <TableHead>Filed</TableHead>
                        <TableHead>Action</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>{pending.map((d) => _renderRow(d, false))}</TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="resolved">
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Resolved history</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <p className="p-6 text-sm text-gray-500">Loading…</p>
                ) : resolved.length === 0 ? (
                  <p className="p-6 text-sm text-gray-500">No resolved disputes yet.</p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Dispute</TableHead>
                        <TableHead>Adjustment</TableHead>
                        <TableHead>Retailer</TableHead>
                        <TableHead>Comment</TableHead>
                        <TableHead>Evidence</TableHead>
                        <TableHead>Filed</TableHead>
                        <TableHead>Outcome</TableHead>
                        <TableHead>Notes</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>{resolved.map((d) => _renderRow(d, true))}</TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        <ResolveModal
          dispute={target}
          onOpenChange={setTarget}
          onResolved={_load}
        />
      </div>
    </AdminLayout>
  );
}
